<script setup lang="ts">
import { useField, useForm } from 'vee-validate'
import { nextTick, ref, watch } from 'vue'
import * as yup from 'yup'
import { updateCommissionAction } from '@/api/functions/settings/get-commissions-action'
import { useMutationApi } from '@/composables/useApi'
import { useToast } from '@/composables/useToast'
import { getErrorMessage } from '@/utils/errorUtils'

const props = defineProps({
  modelValue: Boolean,
  selectedCommission: { type: Object, default: null },
  currencies: { type: Array, default: () => [] },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel'])
const toast = useToast()
// const queryClient = useQueryClient();

// --- Validation Schema ---
const schema = yup.object({
  currency: yup.string().required('انتخاب ارز الزامی است'),
  type: yup.string().required('نوع کارمزد الزامی است'),
  from: yup.number().required('حداقل مبلغ الزامی است').typeError('عدد معتبر وارد کنید'),
  till: yup.number().required('حداکثر مبلغ الزامی است').typeError('عدد معتبر وارد کنید'),
  amount: yup
    .number()
    .required('مقدار کارمزد الزامی است')
    .typeError('عدد معتبر وارد کنید')
    .min(0, 'مقدار کارمزد نمی‌تواند منفی باشد')
    .max(999.99, 'مقدار کارمزد نمی‌تواند بیشتر از 999.99 باشد')
    .test('max-two-decimals', 'حداکثر دو رقم اعشار مجاز است', (value) => {
      if (value === undefined || value === null) return false
      const decimalPart = value.toString().split('.')[1]
      return !decimalPart || decimalPart.length <= 2
    }),
})

// --- Form setup ---
const { handleSubmit, errors, resetForm, setValues } = useForm({
  validationSchema: schema,
  initialValues: {
    currency: '',
    type: 'FIXED',
    from: null,
    till: null,
    amount: null,
  },
})

// Fields
const { value: currency } = useField('currency')
const { value: type } = useField('type')
const { value: from } = useField('from')
const { value: till } = useField('till')
const { value: amount } = useField('amount')
const toEnDigits = (s: string) =>
  s.replace(/[۰-۹]/g, (d) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(d))).replace(/[٠-٩]/g, (d) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(d)))

const formatRate = (raw: unknown) => {
  const s = toEnDigits(String(raw ?? '')).replace(/[^\d.]/g, '')
  const [intPart = '', ...rest] = s.split('.')
  if (!intPart && !rest.length) return ''
  const int = intPart.slice(0, 3)
  const dec = rest.join('').slice(0, 2)
  if (!int) return `0.${dec}`
  return rest.length ? `${int}.${dec}` : int
}

const amountText = ref('')

const onAmountInput = async (raw: string) => {
  amountText.value = raw
  await nextTick()
  amountText.value = formatRate(raw)
  amount.value = amountText.value === '' ? null : amountText.value
}
watch(
  amount,
  (v) => {
    const next = v == null ? '' : String(v)
    if (amountText.value !== next) amountText.value = next
  },
  { immediate: true }
)

// --- Watch selectedCommission and populate form ---
watch(
  () => props.selectedCommission,
  (commission) => {
    if (commission && commission.id) {
      setValues({
        currency: commission.currency?.id || commission.currencyId || '',
        type: commission.type || 'FIXED',
        from: commission.from ?? commission.minAmount ?? null,
        till: commission.till ?? commission.maxAmount ?? null,
        amount: commission.amount ?? commission.feeAmount ?? null,
      })
    } else {
      resetForm()
    }
  },
  { immediate: true, deep: true }
)

// --- Mutation ---
const updateCommissionMutation = useMutationApi({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  mutationFn: ({ id, payload }: { id: string; payload: any }) => updateCommissionAction(id, { ...payload }),
  onSuccess: () => {
    toast.success('کارمزد با موفقیت ویرایش شد')
    emit('confirm')
    closeModal()
    // queryClient.invalidateQueries({ queryKey: ['commissions'] });
  },
  onError: (error: Error) => {
    toast.error(getErrorMessage(error), 'خطا')
  },
})

// --- Submit ---
const onSubmit = handleSubmit((values) => {
  if (!props.selectedCommission?.id) {
    toast.error('کارمزدی برای ویرایش انتخاب نشده است', 'خطا')
    return
  }

  const payload = {
    currency_id: values.currency,
    type: values.type,
    from: values.from,
    till: values.till,
    rate: values.amount == null ? null : Number(values.amount),
  }

  updateCommissionMutation.mutate({
    id: props.selectedCommission.id,
    payload,
  })
})

const closeModal = () => {
  emit('update:modelValue', false)
  emit('cancel')
  resetForm()
}
</script>

<template>
  <Modal :model-value="props.modelValue" @update:model-value="$emit('update:modelValue', $event)" width="600px"
    background="#FFF" :closable="true" title="ویرایش کارمزد" closeDirection="left" class="update-request">

    <form @submit.prevent="onSubmit" class="flex flex-col min-h-[430px]">
      <div class="flex-1 overflow-auto">

        <div class="grid px-4 pt-6 pb-9 gap-2">
          <Select v-model="currency" name="currency" label="ارز" :options="currencies" :error="errors.currency"
            :disabled="updateCommissionMutation.isPending.value" />

          <Select v-model="type" name="type" label="نوع کارمزد" :options="[
            { label: 'مقدار در واحد', value: 'FIXED' },
            { label: 'درصد', value: 'PERCENTAGE' }
          ]" :error="errors.type" :disabled="updateCommissionMutation.isPending.value" />

          <Input v-model="from" name="from" label="از مبلغ" type="number" :error="errors.from"
            :disabled="updateCommissionMutation.isPending.value" />

          <Input v-model="till" name="till" label="تا مبلغ" type="number" :error="errors.till"
            :disabled="updateCommissionMutation.isPending.value" />

          <Input :model-value="amountText" @update:model-value="onAmountInput" name="amount" label="مقدار کارمزد"
            type="text" inputmode="decimal" :error="errors.amount"
            :disabled="updateCommissionMutation.isPending.value" />
        </div>

        <div v-if="updateCommissionMutation.isError.value" class="px-4 mb-4">
          <div class="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
            {{ getErrorMessage(updateCommissionMutation.error.value) }}
          </div>
        </div>
      </div>

      <div class="sticky bottom-0 bg-white z-50 flex justify-between gap-6 w-full pt-3 pb-9 px-2 border-gray-200">
        <Button type="submit" label="ویرایش" :isAdmin="true" class="w-9/12 h-12 rounded-xl"
          :disabled="updateCommissionMutation.isPending.value || !props.selectedCommission?.id"
          :loading="updateCommissionMutation.isPending.value" />
        <Button type="button" @click="closeModal" variant="outline" label="بازگشت" :isAdmin="true"
          class="w-4/12 h-12 rounded-xl border-2" :disabled="updateCommissionMutation.isPending.value" />
      </div>
    </form>
  </Modal>
</template>
